import { AxiosError } from 'axios'

import i18n from '../i18n'

export enum I18nSeverity {
  ERROR = 0,
  INFO = 1,
  WARN = 2,
  SUCCESS = 3,
}

export enum I18nDataType {
  DATE = 0,
  DATE_TIME = 1,
  TIME = 2,
  STRING = 3,
  NUMBER = 4,
}

interface I18nParameter {
  Key: string
  Value: string
  DataType: I18nDataType
}

export interface I18nMessage {
  Key: string
  Parameters?: I18nParameter[]
  Severity: I18nSeverity
}

/**
 * Enriches an Axios error with a localized i18n message from the backend.
 *
 * This interceptor checks for an `x-i18n` header in the error response.
 * If present, it parses the i18n message, translates it using the i18n system,
 * and replaces the first error in `response.data.errors` with the translated message.
 *
 * The original error message is preserved in `extensions.originalMessage` for debugging.
 *
 * @param error - The Axios error to enrich
 * @returns The enriched Axios error with translated message, or the original error if no i18n header exists
 *
 * @example
 * ```typescript
 * // Backend sends: x-i18n: {"Key": "Masterdata__SupplierDisplayIdAlreadyExists", "Parameters": [{"Key": "DisplayId", "Value": "12345"}]}
 * // Result: error.response.data.errors[0].message = "Ein Lieferant mit der Anzeige-ID '12345' existiert bereits."
 * ```
 */
export const enrichErrorWithI18nMessage = (
  error: AxiosError<any>,
): AxiosError => {
  const hasI18nHeader = !!error.response?.headers['x-i18n']

  if (!hasI18nHeader) return error

  try {
    const i18nMessage: I18nMessage = JSON.parse(
      error.response?.headers['x-i18n'],
    )
    const key = `backendMessages.${i18nMessage.Key}`
    const params =
      i18nMessage.Parameters?.reduce((acc, param) => {
        acc[param.Key] = getTransformedI18nValue(param.Value, param.DataType)
        return acc
      }, {} as Record<string, any>) || {}

    const translation = i18n.t(key, params)

    if (
      error.response?.data &&
      typeof error.response.data === 'object' &&
      typeof translation === 'string'
    ) {
      const responseData = error.response.data

      if (!responseData.errors) {
        responseData.errors = []
      }

      const originalMessage = responseData.errors[0]?.message
      responseData.errors[0] = {
        message: translation,
        extensions: {
          originalMessage, // For debugging
        },
      }
    }
  } catch (parseError) {
    console.warn('Failed to parse i18n header:', parseError)
  }

  return error
}

export const getTransformedI18nValue = (
  value: string,
  dataType: I18nDataType,
) => {
  switch (dataType) {
    case I18nDataType.DATE:
      return new Date(value).toLocaleDateString(i18n.language)
    case I18nDataType.DATE_TIME:
      return new Date(value).toLocaleString(i18n.language)
    case I18nDataType.TIME:
      return new Date(value).toLocaleTimeString(i18n.language)
    case I18nDataType.NUMBER:
      return Number(value).toLocaleString(i18n.language)
    case I18nDataType.STRING:
    default:
      return value
  }
}
